import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader, InputDecimal } from '../../components/ui';
import { Panel, Button, buttonClass } from '../../design-system';

// Helper para transformar string vazia ou 0 em null
const emptyToNull = z.preprocess(
  (val) => (val === '' || val === 0 || val === '0' || Number.isNaN(val) ? null : val),
  z.coerce.number().nullable().optional()
);

const leadSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  telefone: z.string().optional(),
  whatsapp: z.string().optional(),
  origemId: emptyToNull,
  etapaId: z.coerce.number().min(1, 'Selecione a etapa'),
  vendedorId: emptyToNull,
  interesseMarca: z.string().optional(),
  interesseModelo: z.string().optional(),
  interesseValorMax: emptyToNull,
  temperatura: z.enum(['QUENTE', 'MORNO', 'FRIO']).default('MORNO'),
});

type LeadFormData = z.infer<typeof leadSchema>;

interface Etapa {
  id: number;
  nome: string;
  cor: string;
}

interface Origem {
  id: number;
  nome: string;
  cor: string;
}

interface Vendedor {
  id: number;
  nome: string;
}

export function LeadForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const isEditing = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [origens, setOrigens] = useState<Origem[]>([]);
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      temperatura: 'MORNO',
      etapaId: parseInt(searchParams.get('etapa') || '0') || undefined,
    },
  });

  // Carregar dados auxiliares
  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      api.get<Etapa[]>('/leads/etapas', { signal: controller.signal }),
      api.get<Origem[]>('/leads/origens', { signal: controller.signal }),
      api.get<Vendedor[]>('/usuarios/vendedores', { signal: controller.signal }).catch(() => ({ data: [] })),
    ])
      .then(([etapasRes, origensRes, vendedoresRes]) => {
        setEtapas(etapasRes.data);
        setOrigens(origensRes.data);
        setVendedores(vendedoresRes.data);

        // Definir primeira etapa se não houver seleção
        if (!searchParams.get('etapa') && etapasRes.data.length > 0) {
          setValue('etapaId', etapasRes.data[0].id);
        }
      })
      .catch((error) => {
        if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
          showError('Erro ao carregar dados');
        }
      });
    return () => controller.abort();
  }, [searchParams, setValue, showError]);

  // Carregar lead para edição
  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    setIsLoading(true);
    api.get<Record<string, unknown>>(`/leads/${id}`, { signal: controller.signal })
      .then((response) => {
        const lead = response.data;
        Object.keys(lead).forEach((key) => {
          if (key in leadSchema.shape) {
            setValue(key as keyof LeadFormData, (lead[key] ?? '') as LeadFormData[keyof LeadFormData]);
          }
        });
      })
      .catch((error) => {
        if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
          showError('Erro ao carregar lead');
        }
      })
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, [id, setValue, showError]);

  const onSubmit = async (data: LeadFormData) => {
    setIsSaving(true);
    try {
      if (isEditing) {
        await api.put(`/leads/${id}`, data);
      } else {
        await api.post('/leads', data);
      }

      showSuccess(isEditing ? 'Lead atualizado com sucesso' : 'Lead cadastrado com sucesso');
      navigate('/crm/leads');
    } catch {
      showError('Erro ao salvar lead');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? 'Editar Lead' : 'Novo Lead'}
        subtitle={isEditing ? 'Altere os dados do lead' : 'Cadastre um novo lead'}
        showBack
        backTo="/crm/leads"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Dados do Lead */}
        <Panel title="Dados do Lead">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Nome *
              </label>
              <input
                type="text"
                {...register('nome')}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
              {errors.nome && (
                <p className="mt-1 text-sm text-rose-600">{errors.nome.message}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Temperatura
              </label>
              <select
                {...register('temperatura')}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="FRIO">Frio</option>
                <option value="MORNO">Morno</option>
                <option value="QUENTE">Quente</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Email
              </label>
              <input
                type="email"
                {...register('email')}
                placeholder="email@exemplo.com"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-rose-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Telefone
              </label>
              <input
                type="text"
                {...register('telefone')}
                placeholder="(00) 0000-0000"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                WhatsApp
              </label>
              <input
                type="text"
                {...register('whatsapp')}
                placeholder="(00) 00000-0000"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>
        </Panel>

        {/* Classificação */}
        <Panel title="Classificação">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Etapa do Funil *
              </label>
              <select
                {...register('etapaId')}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Selecione...</option>
                {etapas.map((etapa) => (
                  <option key={etapa.id} value={etapa.id}>
                    {etapa.nome}
                  </option>
                ))}
              </select>
              {errors.etapaId && (
                <p className="mt-1 text-sm text-rose-600">{errors.etapaId.message}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Origem
              </label>
              <select
                {...register('origemId')}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Selecione...</option>
                {origens.map((origem) => (
                  <option key={origem.id} value={origem.id}>
                    {origem.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Vendedor Responsável
              </label>
              <select
                {...register('vendedorId')}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Selecione...</option>
                {vendedores.map((vendedor) => (
                  <option key={vendedor.id} value={vendedor.id}>
                    {vendedor.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Interesse */}
        <Panel title="Interesse">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Marca de Interesse
              </label>
              <input
                type="text"
                {...register('interesseMarca')}
                placeholder="Ex: Chevrolet"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Modelo de Interesse
              </label>
              <input
                type="text"
                {...register('interesseModelo')}
                placeholder="Ex: Onix LT 1.0"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <Controller
              name="interesseValorMax"
              control={control}
              render={({ field }) => (
                <InputDecimal
                  label="Valor Máximo"
                  prefix="R$ "
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </Panel>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/crm/leads')}
            className={buttonClass('secondary')}
          >
            Cancelar
          </button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
}
